import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  LeftOutlined, RightOutlined, PhoneOutlined, WhatsAppOutlined,
} from '@ant-design/icons';
import config from '../../config';
import { NdoePage, NdoePageHeader, NdoeStatCard, NdoeStats, NdoeLoading, NdoeError, NdoeEmpty } from '../../components/ndoe';
import { buildWaLink } from '../../utils/whatsapp';
import './monActivite.css';

// Date du jour en fuseau Kinshasa (le process Node force aussi Africa/Kinshasa) :
// on envoie start=end=cette date, l'API borne le « jour » dessus.
const pad = (n) => String(n).padStart(2, '0');
const toDayStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const money = (v) => `${Number(v || 0).toLocaleString('fr-FR')} $`;

/**
 * « Mon activité du jour » — tableau de bord PERSO du livreur (constat proprio :
 * les livreurs prennent en charge mais finalisent tard). Rend visible ce qu'il a
 * déclaré + ce qu'il DIT avoir encaissé, cliente par cliente. Auto-scopé côté API
 * (le token identifie le livreur ; aucun id_livreur envoyé).
 */
export default function MonActiviteJour() {
  const navigate = useNavigate();
  const user = useSelector((s) => s.user.currentUser);
  const domain = config.REACT_APP_SERVER_DOMAIN;
  const token = user?.accessToken || user?.token || '';
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const today = useMemo(() => toDayStr(new Date()), []);
  const [jour, setJour] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${domain}/api/mobile-delivery/mine/activite-jour`, {
        params: { start: jour, end: jour }, headers,
      });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.error || 'Impossible de charger votre activité.');
    } finally {
      setLoading(false);
    }
  }, [domain, headers, jour]);

  useEffect(() => { load(); }, [load]);

  const cloture = data?.cloture || null;
  const [clotureEnCours, setClotureEnCours] = useState(false);
  const heureCourte = (iso) => {
    try { return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); }
    catch (e) { return ''; }
  };
  const cloturer = async () => {
    if (clotureEnCours) return;
    setClotureEnCours(true);
    try {
      await axios.post(`${domain}/api/mobile-delivery/mine/cloture-jour`, { start: jour, end: jour }, { headers });
      toast.success('Journée clôturée. Bonne soirée !');
      load();
    } catch (e) {
      // 409 = il reste des livraisons ; le serveur dit lesquelles en nombre.
      toast.error(e?.response?.data?.error || 'Clôture impossible.');
      load();
    } finally {
      setClotureEnCours(false);
    }
  };

  const estAujourdhui = jour === today;
  const decale = (n) => {
    const d = new Date(`${jour}T00:00:00`);
    d.setDate(d.getDate() + n);
    const s = toDayStr(d);
    if (s > today) return; // jamais dans le futur
    setJour(s);
  };
  const libelleJour = estAujourdhui ? "Aujourd'hui" : new Date(`${jour}T00:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });

  const aj = data?.activite_jour;
  const enc = data?.encours;
  const arg = data?.argent;
  const clientes = data?.clientes || [];
  const cp = (o) => (o && o.paires ? `${o.commandes} · ${o.paires} p` : `${(o && o.commandes) || 0}`);

  return (
    <NdoePage>
      <NdoePageHeader
        eyebrow="Livreur"
        title="Mon activité du jour"
        subtitle="Ce que vous avez déclaré et encaissé"
      />

      <div className="mja-daynav">
        <button type="button" className="mja-daynav__btn" onClick={() => decale(-1)} aria-label="Jour précédent"><LeftOutlined /></button>
        <span className="mja-daynav__label">{libelleJour}</span>
        <button type="button" className={`mja-daynav__btn${estAujourdhui ? ' is-disabled' : ''}`} onClick={() => decale(1)} disabled={estAujourdhui} aria-label="Jour suivant"><RightOutlined /></button>
      </div>

      {loading ? <NdoeLoading rows={5} />
        : error ? <NdoeError text={error} onRetry={load} />
          : (
            <>
              {/* Responsabilité : finaliser d'abord (bloc urgent, cliquable vers le hub) */}
              {enc?.a_finaliser > 0 && (
                <button type="button" className="mja-urgent" onClick={() => navigate('/?scope=active')}>
                  <div className="mja-urgent__ic" aria-hidden="true">!</div>
                  <div className="mja-urgent__body">
                    <b>{enc.a_finaliser} livraison{enc.a_finaliser > 1 ? 's' : ''} à finaliser</b>
                    <small>{enc.plus_ancienne_heures > 0 ? `La plus ancienne ouverte depuis ${enc.plus_ancienne_heures} h` : 'À déclarer maintenant'}</small>
                  </div>
                  <span className="mja-urgent__cta">Finaliser →</span>
                </button>
              )}

              {/* Clôture de la journée. Un geste POSITIF : sans lui, la
                  supervision ne distingue pas « il a fini » de « on ne sait
                  pas ». Le bouton n'apparaît que sur aujourd'hui, et seulement
                  quand plus rien n'est à finaliser — clôturer ne doit jamais
                  servir à faire disparaître un backlog. */}
              {estAujourdhui && cloture && (
                cloture.cloture_le ? (
                  <div className="mja-cloture is-faite">
                    <span className="mja-cloture__ic" aria-hidden="true">✓</span>
                    <div>
                      <b>Journée clôturée</b>
                      <small>à {heureCourte(cloture.cloture_le)}</small>
                    </div>
                  </div>
                ) : cloture.peut_cloturer ? (
                  <button
                    type="button"
                    className="mja-cloture"
                    disabled={clotureEnCours}
                    onClick={cloturer}
                  >
                    <span className="mja-cloture__ic" aria-hidden="true">✓</span>
                    <div>
                      <b>{clotureEnCours ? 'Clôture…' : 'Clôturer ma journée'}</b>
                      <small>Tout est finalisé — signalez que vous avez terminé</small>
                    </div>
                  </button>
                ) : null
              )}

              <h3 className="mja-section">Activité du jour</h3>
              <NdoeStats>
                <NdoeStatCard label="Prises en charge" value={cp(aj?.prises_en_charge)} />
                <NdoeStatCard label="Non prises" value={aj?.non_prises || 0} />
                <NdoeStatCard label="Livrées" value={cp(aj?.livrees)} gold />
                <NdoeStatCard label="Retours magasin" value={cp(aj?.retours_magasin)} />
                <NdoeStatCard label="Échanges" value={cp(aj?.echanges)} />
                <NdoeStatCard label="Taux de déclaration" value={`${aj?.taux_declaration_pct ?? 100} %`} />
              </NdoeStats>

              {/* Suivis déclarés encore ouverts (NON urgents) — si présents */}
              {enc && (enc.reportees + enc.essais + enc.echanges_a_livrer + enc.retours_pour_echange) > 0 && (
                <div className="mja-suivis">
                  <span className="mja-suivis__t">Suivis en attente</span>
                  <span className="mja-suivis__chips">
                    {enc.reportees > 0 && <em>{enc.reportees} report{enc.reportees > 1 ? 's' : ''}</em>}
                    {enc.essais > 0 && <em>{enc.essais} essai{enc.essais > 1 ? 's' : ''}</em>}
                    {enc.echanges_a_livrer > 0 && <em>{enc.echanges_a_livrer} échange{enc.echanges_a_livrer > 1 ? 's' : ''} à livrer</em>}
                    {enc.retours_pour_echange > 0 && <em>{enc.retours_pour_echange} retour{enc.retours_pour_echange > 1 ? 's' : ''} pour échange</em>}
                  </span>
                </div>
              )}

              <h3 className="mja-section">Argent déclaré</h3>
              <div className="mja-money">
                <div className="mja-money__c mja-money__c--ink"><span>Vendu</span><b>{money(arg?.total_vendu)}</b></div>
                <div className="mja-money__c mja-money__c--green"><span>Encaissé</span><b>{money(arg?.total_encaisse_declare)}</b></div>
                <div className="mja-money__c"><span>À crédit</span><b className={arg?.credit > 0 ? 'is-credit' : ''}>{money(arg?.credit)}</b></div>
              </div>
              <p className="mja-note">Montants <b>déclarés</b> par vous à la livraison — à rapprocher de la caisse.</p>

              <h3 className="mja-section">Par cliente livrée</h3>
              {clientes.length === 0
                ? <NdoeEmpty icon="🛵" title="Aucune livraison ce jour" text="Vos clientes livrées apparaîtront ici." />
                : (
                  <div className="mja-clients">
                    {clientes.map((c) => (
                      <div className="mja-client" key={c.id_commande}>
                        <div className="mja-client__top">
                          <span className="mja-client__nom">{c.cliente || `Commande #${c.id_commande}`}</span>
                          <span className={`mja-client__tag${c.solde > 0 ? ' is-credit' : ' is-ok'}`}>{c.solde > 0 ? `crédit ${money(c.solde)}` : 'soldé'}</span>
                        </div>
                        <div className="mja-client__meta">
                          {c.paires_livrees} paire{c.paires_livrees > 1 ? 's' : ''} · vendu {money(c.montant_vendu)} · encaissé <b>{money(c.montant_encaisse_declare)}</b>
                        </div>
                        {c.telephone && (
                          <div className="mja-client__contact">
                            <a className="mja-client__tel" href={`tel:${c.telephone}`}><PhoneOutlined /> {c.telephone}</a>
                            <a className="mja-client__wa" href={buildWaLink(c.telephone)} target="_blank" rel="noopener noreferrer" aria-label={`Écrire à ${c.cliente} sur WhatsApp`}><WhatsAppOutlined /> WhatsApp</a>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
            </>
          )}
    </NdoePage>
  );
}
